import type React from 'react';
import { useEffect, useState } from 'react';

import classNames from 'classnames';

import type { SavedSearchItem } from '@iconik-io/iconik-api/src/schema_patched/saved_search';
import {
  httpDeleteFavorites as httpDeleteFavoritesAssets,
  httpPostFavorites as httpPostFavoritesAssets,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  httpDeleteFavorites as httpDeleteFavoritesSearch,
  httpPostFavorites as httpPostFavoritesSearch,
} from '@iconik-io/iconik-api/src/services/search';
import {
  OBJECT_TYPE_COLLECTIONS,
  OBJECT_TYPE_SAVED_SEARCHES,
} from '@iconik-io/iconik-api/src/types/object_types';
import type { CollectionEntityNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/collection';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';

import { ID_COLLECTION } from '../../constants/reducer_ids';
import { segmentTrackEvent } from '../../ducks/analytics_tracking/thunks';
import { cancellable } from '../../ducks/http/actions';
import { errorMessage } from '../../ducks/messages/thunks';
import { updateSavedSearchSuccess } from '../../ducks/saved_search/actions';
import { modifyBranchByID } from '../../ducks/tree/thunks';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import IconButton from '../../ui/icon_button/icon_button';
import { selectUserRoles } from '../../utils/get_user_roles';

import styles from './favourite_button.module.css';

type Props = {
  className?: string;
  title: string;
  id: string;
  favoured: boolean;
  objectType: 'assets' | 'collections' | 'saved_searches';
  onClick?: (
    favouriteValue: boolean,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => void;
};

export function FavouriteButton(props: Props) {
  const { id, title, favoured, onClick, objectType, className } = props;
  const [isFavourite, setIsFavourite] = useState(favoured);
  const dispatch = useAppDispatch();
  const route = useAppSelector((state) => state.routeParams.route);
  const settings = useAppSelector((state) => state.settings.merged);

  // biome-ignore lint/correctness/useExhaustiveDependencies: side effect
  useEffect(() => {
    setIsFavourite(favoured);
  }, [favoured, id]);

  const roles = useAppSelector(selectUserRoles);

  const isEntitySearchItemLoaded = useAppSelector((state) =>
    Boolean(state.entities.search_items?.[id]),
  );

  const entity = useAppSelector((state) => state.entities[objectType]?.[id]);
  const isEntityObjectTypeLoaded = Boolean(entity);

  const isSavedSearchType = (
    testEntity: typeof entity,
  ): testEntity is SavedSearchItem => objectType === OBJECT_TYPE_SAVED_SEARCHES;

  const isCollectionType = (
    testEntity: typeof entity,
  ): testEntity is CollectionEntityNormalized =>
    objectType === OBJECT_TYPE_COLLECTIONS;

  if (
    settings.hide_favourites ||
    (!roles.canWriteFavorites && !roles.canDeleteFavorites)
  ) {
    return null;
  }

  const deleteFavorites =
    objectType === OBJECT_TYPE_SAVED_SEARCHES
      ? () =>
          httpDeleteFavoritesSearch(
            {
              object_ids: [id],
            },
            { index_immediately: 'true' },
          )
      : () =>
          httpDeleteFavoritesAssets(
            {
              object_ids: [id],
              object_type: objectType,
            },
            { index_immediately: 'true' },
          );

  const postFavorites =
    objectType === OBJECT_TYPE_SAVED_SEARCHES
      ? () =>
          httpPostFavoritesSearch(
            {
              object_ids: [id],
            },
            { index_immediately: 'true' },
          )
      : () =>
          httpPostFavoritesAssets(
            {
              object_ids: [id],
              object_type: objectType,
            },
            { index_immediately: 'true' },
          );

  return (
    <IconButton
      className={classNames(styles.button, className)}
      aria={{ label: 'Toggle favorite', selected: isFavourite }}
      onDoubleClick={(event) => {
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
        if (isFavourite) {
          setIsFavourite(false);

          dispatch(cancellable(deleteFavorites(), `favourite_${id}`))
            .then(() => {
              if (onClick) {
                onClick(false, event);
              }

              const entities = { [id]: { favoured: false } };

              if (isEntitySearchItemLoaded) {
                dispatch(
                  updateEntities({
                    search_items: entities,
                  }),
                );
              }

              if (isEntityObjectTypeLoaded) {
                dispatch(
                  updateEntities({
                    [objectType]: entities,
                  }),
                );
                if (isSavedSearchType(entity)) {
                  const newSavedSearch = { ...entity, favoured: false };
                  dispatch(updateSavedSearchSuccess(newSavedSearch));
                }
              }

              if (isCollectionType(entity)) {
                dispatch(
                  modifyBranchByID(ID_COLLECTION, id, {
                    favoured: false,
                  }),
                );
              }

              dispatch(
                segmentTrackEvent({
                  eventName: `Removed From Favorites`,
                  properties: {
                    'Entity Type': objectType,
                  },
                }),
              );
            })
            .catch(() => {
              setIsFavourite(true);
              dispatch(
                errorMessage({
                  text: `Failed to remove ${title} from favorites`,
                }),
              );
            });

          return;
        }
        setIsFavourite(true);

        dispatch(cancellable(postFavorites(), `favourite_${id}`))
          .then(() => {
            if (onClick) {
              onClick(true, event);
            }

            const entities = { [id]: { favoured: true } };

            if (isEntitySearchItemLoaded) {
              dispatch(
                updateEntities({
                  search_items: entities,
                }),
              );
            }

            if (isEntityObjectTypeLoaded) {
              dispatch(
                updateEntities({
                  [objectType]: entities,
                }),
              );
              if (isSavedSearchType(entity)) {
                const newSavedSearch = { ...entity, favoured: true };
                dispatch(updateSavedSearchSuccess(newSavedSearch));
              }
            }

            if (isCollectionType(entity)) {
              dispatch(
                modifyBranchByID(ID_COLLECTION, id, {
                  favoured: true,
                }),
              );
            }

            dispatch(
              segmentTrackEvent({
                eventName: `Added To Favorites`,
                properties: {
                  Route: route,
                  'Entity Type': objectType,
                },
              }),
            );
          })
          .catch(() => {
            setIsFavourite(false);
            dispatch(
              errorMessage({
                text: `Failed to add ${title} to favorites`,
              }),
            );
          });
      }}
      iconName={isFavourite ? 'favorites_added_to' : 'favorites_add_to'}
    />
  );
}
